<template>
    <wrapper-features-templates-operations
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showStepper="!showLoader && activeStep !== 3 && !showAccountsEmptyState && !applyErrorState"
    :activeStep="activeStep"
    :stepperParams="stepperParams"
    :showFooter="!showLoader && activeStep !== 3 && !showAccountsEmptyState && !applyErrorState"
    :footerButtonsParams="footerButtonsParams"
    :scrollValue="scrollValue"
    :feedbackOperationData="feedbackOperationData"
    :tokenTitleAppend="t('transfers.instantBAI.tokenTitle')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="challengeErrorHandler"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Content -->
        <component
        v-if="!showLoader && !applyErrorState && (!showAccountsEmptyState || showPrerequisitesNotMet)"
        :is="activeStepComponent"
        :formData="formData"
        :formTabsList="formTabsList"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :invalidIbanError="invalidIbanError"
        :invalidIbanErrorMessage="invalidIbanErrorMessage"
        :clearForm="clearForm"
        :responseData="responseData"
        :missingPrerequisites="missingPrerequisites"
        :ibanDetails="ibanDetails"
        :validatedIban="validatedIban"
        :estimatedAmountError="estimatedAmountError"
        :applyOwnCustomerInfo="applyOwnCustomerInfo"
        :isFavorite="isFavorite"
        @updateFormData="updateFormDataHandler"
        @requestOperationsTypes="[reuseDependingDrops = null, requestOperationsTypesHandler()]"
        @requestCategories="[reuseDependingDrops = null, requestCategoriesHandler()]"
        @calculateEstimatedAmount="calculateAmountHandler"
        @validateIban="validateIbanHandler"
        @resetShowInputsErrors="showInputsErrors = false"
        @formIsCleared="clearForm = false"
        @scrollTo="scrollValue = $event"
        @openReuseTransferModal="showReuseTransferModal = true"
        @showFavoriteModal="showFavoritesModal = true"
        @newTransfer="newTransferHandler"/>
        <!-- Empty State -->
        <empty-state-generic
        v-if="!showPrerequisitesNotMet && showAccountsEmptyState && !applyErrorState"
        illustration="definitions-accounts-groups"
        :title="t('transfers.instantBAI.noAccountsTitle')"
        :description="t('transfers.instantBAI.noAccountsText')"/>
    </wrapper-features-templates-operations>
    <!-- Reuse Transfer Modal -->
    <reuse-transfer-modal
    v-if="showReuseTransferModal"
    :accountsList="getAccountsBalances"
    :instantTransfer="true"
    @hideMe="showReuseTransferModal = false"
    @reuseTransferRowClicked="selectTransferHandler"/>
    <!-- Favorites modal -->
    <favorites-modal
    v-if="showFavoritesModal"
    :modalParams="{ operationId: responseData.internationalTransferProcess.customerOperation.id, type: isFavorite ? 'remove' : 'create' }"
    @hideMe="showFavoritesModal = false"
    @requestSuccessful="favoriteModalSuccessHandler($event)"
    @requestError="favoriteModalErrorHandler($event)"/>
    <!-- Existing Transfer Alert Modal -->
    <modal-centered-generic
    v-if="showAlertModal"
    :title="t('transfers.instantBAI.alertModal.title')"
    :text="alertMessage"
    :leftButtonText="t('transfers.instantBAI.alertModal.cancel')"
    :rightButtonText="t('transfers.instantBAI.alertModal.advance')"
    @hideMe="showAlertModal = false"
    @rightButtonClicked="internationalTransferHandler"/>
    <!-- Feedback Toast -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import FavoritesModal from '@/components/generic/modals/centered/Favorite'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ReuseTransferModal from '@/components/features/transfers/instantBAI/ReuseTransferModal'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { clearObjectStrings, atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { emailValidator, internationalPhoneNumberValidator } from '@/assets/scripts/validators'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
/* Api */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const {
    requestClientPrerequisites,
    requestAccountsBalances,
    getClientInfoWithoutMasks,
    requestCustomerLimits,
    requestAccountPlafond,
    requestTransferCurrencies,
    calcEstimatedAmount,
    requestIbanDetails,
    requestTransferReasons,
    requestCountriesList,
    requestCountriesNationalityList,
    requestTransferOperationTypes,
    requestTransferCategories,
    requestTransfersExpenses,
    requestInternationalTransfer,
    requestTransferDetail,
    clearFeatureStates
} = mapActions(moduleMapper.TRANSFERS_INSTANT_BAI)

const {
    getAccountsBalances,
    getOperationTypesList,
    getCategoriesList,
    getCustomerLimits,
    getCustomerPlafonds,
    getCountriesList,
    getCountriesNationalityList,
    getCurrenciesList,
    getTransfersReasons,
    getProfileUnmaskedInfo,
    getTransfersExpenses
} = mapGetters(moduleMapper.TRANSFERS_INSTANT_BAI)

/** Props
@params {Object} - Object with favorite info
*/
const props = defineProps({
    params: Object
})

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const missingPrerequisites = ref([])
const showPrerequisitesNotMet = ref(false)
const showReuseTransferModal = ref(false)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !getAccountsBalances.value.length })
const showFavoritesModal = ref(false)
const showAlertModal = ref(false)
const alertMessage = ref(null)

/*************************************************
*                                                *
*                  Steps Logic                   *
*                                                *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
    { description: t('transfers.instantBAI.step1.title') },
    { description: t('transfers.instantBAI.step2.title') }
])

const activeStepComponent = computed(() => {
    if (showPrerequisitesNotMet.value) return defineAsyncComponent(() => import('@/components/features/transfers/instantBAI/PrerequisitesNotMet'))

    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/transfers/instantBAI/Step' + stepNumber))
})

const changeStepHandler = stepNumber => {
    activeStep.value = stepNumber
    scrollValue.value = 0
}

const newTransferHandler = () => {
    isFavorite.value = false

    changeStepHandler(1)
    clearAllFields()
    initialRequests()
}

/*************************************************
*                                                *
*                   Form Data                    *
*                                                *
*************************************************/
const scrollValue = ref(null)
const clearForm = ref(false)
const showInputsErrors = ref(false)
const reuseDependingDrops = ref(null)
const ibanDetails = ref({})
const validatedIban = ref(false)
const estimatedAmountError = ref(false)
const recreateTransfer = ref(false)
const internationalTransferId = ref(null)
const mdwRawFieldsWithError = ref([])
const fieldsWithError = ref([])
const invalidIbanError = ref(false)
const invalidIbanErrorMessage = ref(null)

const formData = ref({
    /* Account Data */
    originAccount: '',
    accountDisplayInfo: '',
    accountCurrency: '',
    /* Transfers Operation Types Data */
    internationalTransferReasonLeafId: '',
    internationalTransferOperationTypeId: '',
    internationalTransferPaymentCategoryId: '',
    internationalTransferExpenseId: '',
    /* Amount Data */
    internationalTransferCurrencyExternalId: 'EUR',
    amount: '',
    estimatedAmount: '',
    withComission: '',
    /* Transfer Form Data */
    accountInternationalNumber: '',
    swiftCode: '',
    name: '',
    fiscalDocumentNumber: '',
    nationality: '',
    phoneNumber: '',
    email: '',
    internationalTransferCountryId: '',
    zipCode: '',
    city: '',
    address: '',
    door: '',
    floor: ''
})

const mandatoryList = ref([
    /* Account Data */
    'originAccount',
    /* Transfers Operation Types Data */
    'internationalTransferReasonLeafId',
    'internationalTransferOperationTypeId',
    'internationalTransferPaymentCategoryId',
    'internationalTransferExpenseId',
    /* Amount Data */
    'internationalTransferCurrencyExternalId',
    'amount',
    /* Transfer Form Data */
    'accountInternationalNumber',
    'name',
    'fiscalDocumentNumber',
    'nationality',
    'internationalTransferCountryId',
    'city',
    'address',
    'door'
])

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue

    if (field === 'originAccount') {
        const selectedAccount = getAccountsBalances.value.find(obj => obj.id === inputValue)

        formData.value.accountCurrency = selectedAccount ? selectedAccount.currency : ''
        formData.value.estimatedAmount = ''
        formData.value.withComission = ''

        requestPlafondsAndLimits()
    }

    if (field === 'accountInternationalNumber') {
        validatedIban.value = false
    }

    if (field === 'amount' || field === 'internationalTransferCurrencyId') {
        formData.value.estimatedAmount = ''
        formData.value.withComission = ''
    }
}

const applyOwnCustomerInfo = computed(() => {
    if (!getTransfersReasons.value.length || formData.value.internationalTransferOperationTypeId === '') return false

    const operationTypeSelected = getOperationTypesList.value.find(obj => obj.id === formData.value.internationalTransferOperationTypeId)
    return operationTypeSelected ? !operationTypeSelected.senderNameEditable : false
})

const saveDropsInfo = () => {
    reuseDependingDrops.value = {
        internationalTransferOperationTypeId: formData.value.internationalTransferOperationTypeId,
        internationalTransferPaymentCategoryId: formData.value.internationalTransferPaymentCategoryId
    }
}

const validateEstimatedAmount = () => {
    /* We check to see if User amount is higher than the amount allowed in his account and if so we set error to true */
    if (getCustomerLimits.value.remainingAmount < formData.value.estimatedAmount || getCustomerPlafonds.value.plafond < formData.value.estimatedAmount) {
        estimatedAmountError.value = true
    }
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
const validateInputs = () => {
    if (!validatedIban.value && !!formData.value.accountInternationalNumber.length) {
        noValidatedIbanToast()
        return
    }

    const genericDestination = formData.value.accountInternationalNumber
    let mandatoryListParsed = mandatoryList.value

    if (applyOwnCustomerInfo.value) {
        mandatoryListParsed = mandatoryListParsed.filter(string => string !== 'name' && string !== 'fiscalDocumentNumber')
    }

    const inputErrorValidation =
		!genericDestination ||
		Number(formData.value.amount) === 0 ||
		atLeastOneIsEmpty(formData.value, mandatoryListParsed) ||
		!validatedIban.value ||
		estimatedAmountError.value ||
        formData.value.fiscalDocumentNumber.toString().length < 5 ||
		formData.value.name.length < 5 ||
		(formData.value.email.length && !emailValidator(formData.value.email)) ||
		(formData.value.phoneNumber.length && !internationalPhoneNumberValidator(formData.value.phoneNumber)) ||
        (formData.value.name.length < 5 || formData.value.name.trim().indexOf(' ') === -1)

    if (inputErrorValidation) {
        showInputsErrors.value = true
    } else {
        if (formData.value.estimatedAmount && formData.value.withComission) {
            changeStepHandler(2)
        } else calculateAmountHandler('nextStepCall')
    }
}

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
    return [{
        label: t('transfers.instantBAI.footerButtons.clear'),
        buttonPosition: 'left',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { clearAllFields() }
    }, {
        label: t('transfers.instantBAI.footerButtons.forward'),
        icon: 'arrow-right-full',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 1,
        callbackFunction: () => { validateInputs() }
    }, {
        label: t('transfers.instantBAI.footerButtons.edit'),
        icon: 'arrow-left-full',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { changeStepHandler(1) }
    }, {
        label: t('transfers.instantBAI.footerButtons.validate'),
        icon: 'check-line',
        buttonType: 'primary',
        iconPosition: 'right',
        hidden: activeStep.value !== 2,
        callbackFunction: () => { internationalTransferHandler() }
    }]
})

const footerButtonsParams = computed(() => {
    return footerButtonsData.value.filter(ele => !ele.hidden)
})

const clearAllFields = () => {
    formData.value = clearObjectStrings(formData.value)

    formData.value = {
        ...formData.value,
        originAccount: getAccountsBalances.value.find(account => account.default).id,
        accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
        accountCurrency: getAccountsBalances.value.find(account => account.default).currency,
        internationalTransferCurrencyExternalId: 'EUR'
    }

    validatedIban.value = false
    showInputsErrors.value = false
    estimatedAmountError.value = false
    clearForm.value = true
    scrollValue.value = 0
    responseData.value = null
    feedbackOperationData.value = null
    reuseDependingDrops.value = null
    isFavorite.value = false

    if (getTransfersExpenses.value.length === 1) {
        const option = getTransfersExpenses.value.find(obj => obj).id
        formData.value.internationalTransferExpenseId = option
    }

    if (getTransfersReasons.value.length === 1) {
        const option = getTransfersReasons.value.find(obj => obj).id

        formData.value.internationalTransferReasonLeafId = option

        requestOperationsTypesHandler()
    }
}

/*************************************************
*                                                *
*               Reuse Transfer Logic             *
*                                                *
*************************************************/
const getSelectedFavoriteValues = (selectedFavorite, name) => {
    return selectedFavorite.find(obj => obj.name === name)?.value
}

const favoriteDataParser = favoriteData => {
    let data = {}

    const selectedFavorite = favoriteData.customerOperationView.parameters

    const beneficiaryInfo = JSON.parse(getSelectedFavoriteValues(selectedFavorite, 'internationalBeneficiary'))
    const accountNumber = getSelectedFavoriteValues(selectedFavorite, 'accountId')
    const beneficiaryNationalityCode = getSelectedFavoriteValues(selectedFavorite, 'beneficiaryNationalityCode')
    const amount = getSelectedFavoriteValues(selectedFavorite, 'amount')
    const currencyId = getSelectedFavoriteValues(selectedFavorite, 'internationalTransferCurrencyId')
    const operationPurposeId = getSelectedFavoriteValues(selectedFavorite, 'internationalTransferOperationPurpose')
    const operationTypeId = getSelectedFavoriteValues(selectedFavorite, 'internationalTransferOperationType')
    const operationCategoryId = getSelectedFavoriteValues(selectedFavorite, 'internationalTransferOperationCategory')
    const expensesTypeId = getSelectedFavoriteValues(selectedFavorite, 'internationalTransferExpenseId')

    data = {
        beneficiary: { ...beneficiaryInfo },
        originAccount: { accountNumber: Number(accountNumber) },
        amount: amount,
        currency: { id: Number(currencyId) },
        operationPurpose: { id: Number(operationPurposeId) },
        operationType: { id: Number(operationTypeId) },
        operationCategory: { id: Number(operationCategoryId) },
        expensesType: { id: Number(expensesTypeId) },
        beneficiaryNationalityCode: beneficiaryNationalityCode
    }

    return data
}

const selectTransferHandler = async (transfer, isFirstRequest) => {
    clearForm.value = true

    validatedIban.value = !!transfer.beneficiary.accountInternationalNumber.length

    /* If we receive an account id */
    if (getAccountsBalances.value.find(account => account.id === transfer.originAccount.accountNumber)) {
        formData.value.originAccount = getAccountsBalances.value.find(account => account.id === transfer.originAccount.accountNumber).id
    /* If we receive an account number */
    } else if (getAccountsBalances.value.find(account => account.accountNumber === transfer.originAccount.accountNumber)) {
        formData.value.originAccount = getAccountsBalances.value.find(account => account.accountNumber === transfer.originAccount.accountNumber).id
    }

    formData.value.amount = transfer.amount ? Number(transfer.amount) : ''
    formData.value.internationalTransferCurrencyExternalId = getCurrenciesList.value.find(currency => currency.id === transfer.currency.id).externalId || ''
    formData.value.internationalTransferReasonLeafId = transfer.operationPurpose.id || ''

    reuseDependingDrops.value = {
        internationalTransferOperationTypeId: transfer.operationType.id || '',
        internationalTransferPaymentCategoryId: transfer.operationCategory.id || ''
    }

    formData.value.internationalTransferExpenseId = transfer.expensesType.id || ''

    if (!formData.value.originAccount) {
        formData.value = {
            ...formData.value,
            originAccount: getAccountsBalances.value.find(account => account.default).id,
            accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
            accountCurrency: getAccountsBalances.value.find(account => account.default).currency
        }
    }

    await requestOperationsTypesHandler(isFirstRequest, transfer.beneficiary)

    if (transfer.beneficiaryNationalityCode) {
        formData.value.nationality = getCountriesNationalityList.value.find(country => country.externalId === transfer.beneficiaryNationalityCode).id
    } else formData.value.nationality = ''

    reuseFormDataInfo(transfer.beneficiary)

    showReuseTransferModal.value = false
}

const reuseFormDataInfo = beneficiaryData => {
    formData.value.accountInternationalNumber = beneficiaryData.accountInternationalNumber || ''
    formData.value.swiftCode = beneficiaryData.swiftCode || ''
    formData.value.name = beneficiaryData.name || ''
    formData.value.fiscalDocumentNumber = beneficiaryData.fiscalDocumentNumber || ''
    formData.value.phoneNumber = beneficiaryData.phoneNumber && beneficiaryData.phoneNumber !== '-' ? beneficiaryData.phoneNumber : ''
    formData.value.email = beneficiaryData.email && beneficiaryData.email !== '-' ? beneficiaryData.email : ''
    formData.value.internationalTransferCountryId = beneficiaryData.internationalTransferCountryId || beneficiaryData.country.id
    formData.value.zipCode = beneficiaryData.zipCode && beneficiaryData.zipCode !== '0000' ? beneficiaryData.zipCode : ''
    formData.value.city = beneficiaryData.city || ''
    formData.value.address = beneficiaryData.address || ''
    formData.value.door = beneficiaryData.door || ''
    formData.value.floor = beneficiaryData.floor && beneficiaryData.floor !== '999999999' ? beneficiaryData.floor : ''

    if (!getTransfersReasons.value.find(reason => reason.id === formData.value.internationalTransferReasonLeafId)) formData.value.internationalTransferReasonLeafId = ''

    if (!getOperationTypesList.value.find(reason => reason.id === formData.value.internationalTransferOperationTypeId)) formData.value.internationalTransferOperationTypeId = ''

    if (!getCategoriesList.value.find(reason => reason.id === formData.value.internationalTransferPaymentCategoryId)) formData.value.internationalTransferPaymentCategoryId = ''

    if (!getTransfersExpenses.value.find(reason => reason.id === formData.value.internationalTransferExpenseId)) formData.value.internationalTransferExpenseId = ''
}

/*************************************************
*                                                *
*                   Requests                     *
*                                                *
*************************************************/
const checkClientPrerequisites = async () => {
    cancelAllPendingRequests()

    showLoader.value = true
    applyErrorState.value = false

	try {
        await requestClientPrerequisites()

        initialRequests()
	} catch (error) {
        if (error.toString() === 'Cancel') return
        if (error.response.data.code === 'invalid_parameters') {
            missingPrerequisites.value = error.response.data.invalidParametersErrorMessageList
            showPrerequisitesNotMet.value = true
            showLoader.value = false
            return
        }
        applyErrorState.value = true
        errorHandler(error.response.data, checkClientPrerequisites)
	}
}

const initialRequests = async () => {
    showLoader.value = true
    applyErrorState.value = false

    Promise.all([
        requestAccountsBalances(),
        getClientInfoWithoutMasks(),
        requestCustomerLimits(),
        requestTransferCurrencies(),
        requestCountriesList({ isInstant: true }),
        requestCountriesNationalityList(),
        requestTransferReasons(),
        requestTransfersExpenses()
    ]).then(() => {
        if (getAccountsBalances.value.length) {
            /* Setting Initial Selected Account */
            formData.value.originAccount = getAccountsBalances.value.find(account => account.default).id

            /* Setting Initial Account Display Info from Default Account */
            formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.default).accountNumber

            /* Setting Initial Currency from Default Account */
            formData.value.accountCurrency = getAccountsBalances.value.find(account => account.default).currency
        }

        if (formData.value.originAccount) requestPlafondsAndLimits('isFirstRequest')

        if (getTransfersExpenses.value.length === 1) {
            const option = getTransfersExpenses.value.find(obj => obj).id

            formData.value.internationalTransferExpenseId = option || null
        }

        if (props.params) {
            selectTransferHandler(favoriteDataParser(props.params), 'isFirstRequest')
        } else if (getTransfersReasons.value.length === 1) {
            const option = getTransfersReasons.value.find(obj => obj).id

            formData.value.internationalTransferReasonLeafId = option || null

            requestOperationsTypesHandler('isFirstRequest')
        } else showLoader.value = false
    }).catch(error => {
        if (error.toString() === 'Cancel') return
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequests)
    })
}

const requestOperationsTypesHandler = async (isFirstRequest, beneficiaryData) => {
    /* First we validate if we have what we need to requests operation types */
    if (!formData.value.internationalTransferReasonLeafId) {
        showLoader.value = false
        return
    }

    /* If we are not reusing any values, we will clear values depending on this one */
    if (!reuseDependingDrops.value) {
        formData.value.internationalTransferOperationTypeId = ''
        formData.value.internationalTransferPaymentCategoryId = ''
    }

    if (!isFirstRequest) showOverlayLoader.value = true

    const payload = {
        purposeId: formData.value.internationalTransferReasonLeafId
    }

	try {
       await requestTransferOperationTypes(payload)
       if (reuseDependingDrops.value) {
            formData.value.internationalTransferOperationTypeId = reuseDependingDrops.value.internationalTransferOperationTypeId

            requestCategoriesHandler(isFirstRequest, beneficiaryData)
        } else if (getOperationTypesList.value.length === 1) {
            const option = getOperationTypesList.value.find(obj => obj).id

            formData.value.internationalTransferOperationTypeId = option

            requestCategoriesHandler(isFirstRequest)
        } else {
            showOverlayLoader.value = false
            showLoader.value = false
        }
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestOperationsTypesHandler)
	}
}

const requestCategoriesHandler = async (isFirstRequest, beneficiaryData) => {
    /* First we validate if we have what we need to requests categories */
    if (!formData.value.internationalTransferOperationTypeId && !formData.value.internationalTransferOperationTypeId.length) return

    /* If we are not reusing any values, we will clear values depending on this one */
    if (!reuseDependingDrops.value) formData.value.internationalTransferPaymentCategoryId = ''

    if (!isFirstRequest) showOverlayLoader.value = true

    const payload = { operationTypeId: formData.value.internationalTransferOperationTypeId }

	try {
       await requestTransferCategories(payload)
       if (reuseDependingDrops.value) {
           formData.value.internationalTransferPaymentCategoryId = reuseDependingDrops.value.internationalTransferPaymentCategoryId
        }
        if (getCategoriesList.value.length === 1) {
            const option = getCategoriesList.value.find(obj => obj).id
            formData.value.internationalTransferPaymentCategoryId = option
        }

        if (!beneficiaryData) {
            formData.value.name = applyOwnCustomerInfo.value ? getProfileUnmaskedInfo.value.personalData.name : ''
            formData.value.fiscalDocumentNumber = applyOwnCustomerInfo.value ? getProfileUnmaskedInfo.value.fiscalDocument.fiscalNumber : ''
        }

        showOverlayLoader.value = false
        showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestCategoriesHandler)
	}
}

const calculateAmountHandler = async functionCaller => {
    showOverlayLoader.value = true
    estimatedAmountError.value = false

    const payload = {
        value: formData.value.amount,
        currencyOrigin: formData.value.internationalTransferCurrencyExternalId,
        currencyDestination: formData.value.accountCurrency,
        isInstant: true
    }

	try {
        const response = await calcEstimatedAmount(payload)

        formData.value.estimatedAmount = response.data.data.totalOperationValue
        formData.value.withComission = response.data.data.withComission

        validateEstimatedAmount()

        /* If nextStepCall is called we check if we have estimatedAmountError if not we go to next step */
        if (functionCaller === 'nextStepCall' && !estimatedAmountError.value) {
            saveDropsInfo()
            changeStepHandler(2)
        }

        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, calculateAmountHandler)
	}
}

const requestPlafondsAndLimits = async isFirstRequest => {
    if (!isFirstRequest) showOverlayLoader.value = true

	try {
        await requestAccountPlafond({ accountId: formData.value.originAccount, isInstant: true })

        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, requestPlafondsAndLimits)
	}
}

const validateIbanHandler = async () => {
    showOverlayLoader.value = true
    validatedIban.value = false
    invalidIbanError.value = false
    invalidIbanErrorMessage.value = null

    const payload = { iban: formData.value.accountInternationalNumber }

	try {
        const response = await requestIbanDetails(payload)

        ibanDetails.value = response.data.data.internationalTransferIBANDetails

        /* Response Data */
        const responseData = ibanDetails.value.financialInstitutionDetails
        /* Response data Iban Details */
        const responseDataDetails = responseData.internationalTransferIBANDetailsFinancialInstitutionContacts[0]
        /* We use this validation in case MDW doenst send CountryId we go to Countries list and find it by the description */
        const internationalTransferCountryId = responseDataDetails.country.id ? responseDataDetails.country.id : getCountriesList.value.find(obj => obj.description === responseDataDetails.postalCodeDescription).id
        /* Applying Beneficiary Data */
        formData.value.swiftCode = responseData.identification ? responseData.identification : ''
        formData.value.internationalTransferCountryId = internationalTransferCountryId
        formData.value.city = responseDataDetails.city ? responseDataDetails.city : ''
        validatedIban.value = true

        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, validateIbanHandler)
        invalidIbanErrorMessage.value = error.response.data.message
        invalidIbanError.value = true
	}
}

const internationalTransferHandler = async () => {
    showOverlayLoader.value = true

    const payload = {
        beneficiary: {
            ...formData.value,
            name: formData.value.name,
            fiscalDocumentNumber: formData.value.fiscalDocumentNumber,
            birthDate: null
        },
        accountId: formData.value.originAccount,
        amount: formData.value.amount,
        internationalTransferCurrencyId: getCurrenciesList.value.find(currency => currency.externalId === formData.value.internationalTransferCurrencyExternalId).id,
        internationalTransferReasonLeafId: formData.value.internationalTransferPaymentCategoryId,
        internationalTransferExpenseId: formData.value.internationalTransferExpenseId,
        description: `OPE ${formData.value.name}`,
        recreate: recreateTransfer.value,
        currencyDestination: formData.value.internationalTransferCurrencyExternalId,
        withComission: formData.value.withComission,
        beneficiaryAccountNumber: formData.value.accountInternationalNumber,
        beneficiaryNationalityCode: getCountriesNationalityList.value.find(country => country.id === formData.value.nationality).externalId
    }

    if (internationalTransferId.value) payload.internationalTransferId = internationalTransferId.value

	try {
       await requestInternationalTransfer(payload)
       recreateTransfer.value = false
       showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        /* If MDW sends a list of errors from form we will show the errors if user returns to step 1 */
        if (error.response.data.invalidParametersErrorMessageList && error.response.data.invalidParametersErrorMessageList.length) {
            mdwRawFieldsWithError.value = error.response.data.invalidParametersErrorMessageList
            showOverlayLoader.value = false

            feedbackToastData.value = errorObjectConstructor(error.response.data, internationalTransferHandler, () => { applyStep1FieldsError() })
            showFeedbackToast.value = true
            return
        }
        /* If MDW says a similar transfer exists we need to alert our user */
        if (error.response.data.code === 'similar_transfer_already_exists') {
            alertMessage.value = error.response.data.message
            recreateTransfer.value = true
            showOverlayLoader.value = false
            showAlertModal.value = true
            return
        }
        /* If another error is received we do the usual behaviour */
        errorHandler(error.response.data, internationalTransferHandler, () => { changeStepHandler(1) })
	}
}

const checkTransferStatus = async () => {
    showOverlayLoader.value = true

    try {
        const res = await requestTransferDetail(internationalTransferId.value)

        if (res.data.data &&
            (res.data.data.internationalTransferProcessView.status.id !== 'STATE001' && res.data.data.internationalTransferProcessView.status.id !== 'A')) {
            responseData.value = {
                errorLevel: res.data.errorLevel,
                code: res.data.code,
                internationalTransferProcess: {
                    ...res.data.data.internationalTransferProcessView,
                    additionalMessage: t('transfers.instantBAI.step3.feAdditionalMessage', { externalId: res.data.data.internationalTransferProcessView.externalId })
                },
                message: t('transfers.instantBAI.step3.successOperation')
            }

            feedbackOperationData.value = {
                status: responseData.value.errorLevel,
                title: responseData.value.message,
                additionalMessage: responseData.value.additionalMessage || '',
                buttonsData: !responseData.value.hasReceipt ? null : {
                    emailAction: 'sendOperationEmailNotification',
                    smsAction: 'sendOperationSMSNotification',
                    sendPayload: { operationId: responseData.value.data.operationId },
                    downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: responseData.value.operationId } }),
                    operationId: responseData.value.operationId
                }
            }

            changeStepHandler(3)
            showOverlayLoader.value = false
        } else internationalTransferHandler()
    } catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, checkTransferStatus)
	}
}

/*************************************************
*                                                *
*               Challenge Handlers               *
*                                                *
*************************************************/
const responseData = ref(null)
const feedbackOperationData = ref(null)
const isFavorite = ref(false)

const challengeSuccessHandler = data => {
    responseData.value = {
        ...data.data,
        errorLevel: data.errorLevel,
        code: data.code
    }

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        additionalMessage: data.additionalMessage || '',
        buttonsData: data.data.internationalTransferProcess.hasReceipt ? null : {
            emailAction: 'sendOperationEmailNotification',
            smsAction: 'sendOperationSMSNotification',
            sendPayload: { operationId: data.data.internationalTransferProcess.customerOperation.id },
            downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.internationalTransferProcess.customerOperation.id } }),
            operationId: data.data.internationalTransferProcess.customerOperation.id
        }
    }

    changeStepHandler(3)
}

const challengeErrorHandler = response => {
    /* this was written based on mdw wiki, since we can't test it */
    internationalTransferId.value = response.internationalTransferId

    feedbackToastData.value = errorObjectConstructor(
        response,
        response.internationalTransferId ? checkTransferStatus : internationalTransferHandler,
        () => { changeStepHandler(1) }
    )
    showFeedbackToast.value = true
    showOverlayLoader.value = false
}

/*************************************************
*                                                *
*                  Error Handlers                *
*                                                *
*************************************************/
const applyErrorState = ref(false)
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const applyStep1FieldsError = () => {
    fieldsWithError.value = mdwRawFieldsWithError.value.map(item => {
        return {
            ...item,
            parsedFieldOnError: item.fieldOnError.includes('beneficiary.') ? item.fieldOnError.replace('beneficiary.', '') : item.fieldOnError
        }
    })

    changeStepHandler(1)
    /* This is to give time to step 1 to render */
    setTimeout(() => { showInputsErrors.value = true }, 300)
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { changeStepHandler(1) })
	showFeedbackToast.value = true
}

const favoriteModalErrorHandler = (response) => {
    errorHandler(response)
    showFavoritesModal.value = false
}

const sucessFeedbackToastHandler = data => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const favoriteModalSuccessHandler = (response) => {
    isFavorite.value = !isFavorite.value
    sucessFeedbackToastHandler(response)
    showFavoritesModal.value = false
}

const noValidatedIbanToast = () => {
    showFeedbackToast.value = true

    feedbackToastData.value = {
        toastType: 'negative',
        text: t('transfers.instantBAI.step1.noValidatedIbanToast.text'),
        buttons: [{
            text: t('transfers.instantBAI.step1.noValidatedIbanToast.buttonText'),
            callbackAction: () => { showFeedbackToast.value = false }
        }]
    }
}

checkClientPrerequisites()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>
